import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button, SearchInput, Select } from '../../components/ui'
import { insertError, slideInsertsAPI } from './api'
import type { ScopeSearchItem, ScopeSearchMatch, ScopeSearchResult } from './types'

const labels = { wb: 'ВБ', ozon: 'ОЗОН', ym: 'Маркет', site: 'Сайт' } as const
const matchLabels = {
  design: 'Макет',
  product: 'Товар',
  card: 'Карточка',
  theme: 'Тематика',
  bundle: 'Склейка',
  name: 'Название',
}

function MatchPath({ match }: { match: ScopeSearchMatch }) {
  const path = [
    match.cardCode && `карточка ${match.cardCode}`,
    match.article && `товар ${match.article}`,
    match.designCode && `макет ${match.designCode}`,
    match.themeNumber && `тематика № ${match.themeNumber}`,
    match.marketplace && labels[match.marketplace],
    match.sellerName,
  ].filter(Boolean)
  return (
    <p className="break-words text-xs text-gray-500 dark:text-zinc-400">
      <span className="font-medium">{matchLabels[match.type]}:</span> <code>{match.value}</code>
      {path.length > 0 && <span> · {path.join(' → ')}</span>}
    </p>
  )
}

function ScopeTitle({ row }: { row: ScopeSearchItem }) {
  return (
    <>
      <strong className="block text-sm">
        {row.title}
        {row.themeNumber && ` · № ${row.themeNumber}`}
      </strong>
      <span className="block text-xs text-gray-500 dark:text-zinc-400">
        {[
          row.kind === 'bundle' && `ID ${row.id}`,
          row.marketplace && labels[row.marketplace],
          row.sellerName &&
            (row.sellerId ? `${row.sellerName} · кабинет № ${row.sellerId}` : row.sellerName),
          row.externalBundleId && `код склейки ${row.externalBundleId}`,
          row.categories.map((c) => `${c.code} ${c.name}`).join(', '),
        ]
          .filter(Boolean)
          .join(' · ') || (row.kind === 'theme' ? 'Все карточки тематики' : `Склейка ID ${row.id}`)}
      </span>
    </>
  )
}

interface Props {
  kind: 'theme' | 'bundle'
  bundleIds?: number[]
  themeNumber?: number
  onChange: (value: { bundleIds?: number[]; themeNumber?: number }) => void
  sellers: Array<{ id: string | number; name: string; marketplace: string }>
  categories: Array<{ id: number; name: string }>
  disabled?: boolean
}

/** Search filters describe results only. Selected IDs are independent and never inferred from a query. */
export function ScopePicker({
  kind,
  bundleIds,
  themeNumber,
  onChange,
  sellers,
  categories,
  disabled,
}: Props) {
  const [query, setQuery] = useState('')
  const [marketplace, setMarketplace] = useState('')
  const [sellerId, setSellerId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [offset, setOffset] = useState(0)
  const [page, setPage] = useState<ScopeSearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cache, setCache] = useState<Record<string, ScopeSearchItem>>({})
  const request = useRef(0)
  const ids = kind === 'theme' ? (themeNumber ? [themeNumber] : []) : bundleIds || []
  const selectionKey = ids.join(',')
  const remember = (rows: ScopeSearchItem[]) =>
    setCache((old) => {
      const next = { ...old }
      rows.forEach((row) => {
        next[`${row.kind}:${row.kind === 'theme' ? (row.themeNumber ?? row.id) : row.id}`] = row
      })
      return next
    })
  useEffect(() => {
    setOffset(0)
  }, [query, marketplace, sellerId, categoryId, kind])
  useEffect(() => {
    const controller = new AbortController()
    const version = ++request.current
    setLoading(true)
    setError('')
    setPage(null)
    const timer = setTimeout(() => {
      void slideInsertsAPI
        .scopeSearch(
          {
            scopeKind: kind,
            q: query.trim() || undefined,
            marketplace: (marketplace as 'wb' | 'ozon' | 'ym' | 'site') || undefined,
            sellerId: sellerId ? Number(sellerId) : undefined,
            categoryId: categoryId ? Number(categoryId) : undefined,
            limit: 20,
            offset,
          },
          controller.signal,
        )
        .then((data) => {
          if (controller.signal.aborted || request.current !== version) return
          setPage(data)
          remember(data.items)
          setLoading(false)
        })
        .catch((e) => {
          if (controller.signal.aborted || request.current !== version) return
          setError(insertError(e))
          setLoading(false)
        })
    }, 300)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, marketplace, sellerId, categoryId, kind, offset])
  useEffect(() => {
    if (!selectionKey) return
    const controller = new AbortController()
    const selectedIds = selectionKey.split(',')
    const chunks = Array.from({ length: Math.ceil(selectedIds.length / 50) }, (_, i) =>
      selectedIds.slice(i * 50, (i + 1) * 50).join(','),
    )
    void Promise.all(
      chunks.map((ids) =>
        slideInsertsAPI.scopeSearch({ scopeKind: kind, ids, limit: 50 }, controller.signal),
      ),
    )
      .then((pages) => {
        if (!controller.signal.aborted) remember(pages.flatMap((page) => page.items))
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(insertError(e))
      })
    return () => controller.abort()
  }, [kind, selectionKey])
  const selected = (row: ScopeSearchItem) =>
    ids.includes(kind === 'theme' ? (row.themeNumber ?? row.id) : row.id)
  function choose(row: ScopeSearchItem) {
    remember([row])
    if (kind === 'theme') onChange({ themeNumber: row.themeNumber ?? row.id })
    else
      onChange({ bundleIds: selected(row) ? ids.filter((id) => id !== row.id) : [...ids, row.id] })
  }
  function filter(set: (value: string) => void, value: string) {
    setOffset(0)
    set(value)
  }
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium">Найти по макету, товару или карточке</label>
      <SearchInput
        hotkey={false}
        onClear={() => filter(setQuery, '')}
        value={query}
        onChange={(value) => filter(setQuery, value)}
        placeholder="Номер макета, артикул товара или код карточки"
        aria-label="Номер макета, артикул товара или код карточки"
      />
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Введите код или название, затем выберите{' '}
        {kind === 'theme' ? 'одну тематику' : 'одну или несколько склеек'}. Поиск сам не меняет
        охват.
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-xs">
          Площадка для поиска
          <Select
            value={marketplace}
            onChange={(e) => {
              filter(setMarketplace, e.target.value)
              setSellerId('')
            }}
          >
            <option value="">Все площадки</option>
            {Object.entries(labels).map(([value, title]) => (
              <option key={value} value={value}>
                {title}
              </option>
            ))}
          </Select>
        </label>
        <label className="text-xs">
          Кабинет для поиска
          <Select value={sellerId} onChange={(e) => filter(setSellerId, e.target.value)}>
            <option value="">Все кабинеты</option>
            {sellers
              .filter((s) => !marketplace || s.marketplace === marketplace)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · кабинет № {s.id}
                </option>
              ))}
          </Select>
        </label>
        <label className="text-xs">
          Носитель для поиска
          <Select value={categoryId} onChange={(e) => filter(setCategoryId, e.target.value)}>
            <option value="">Все носители</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <div aria-live="polite" className="text-xs">
        {loading ? 'Ищем карточки и связи…' : page ? `Найдено: ${page.total}` : null}
        {error && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
      <div
        className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-zinc-700"
        aria-label="Результаты поиска охвата"
      >
        {page?.items.map((row) => (
          <div
            key={`${row.kind}:${row.id}`}
            className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 p-3 last:border-b-0 dark:border-zinc-800 ${selected(row) ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''}`}
          >
            <input
              id={`scope-result-${kind}-${row.id}`}
              type={kind === 'theme' ? 'radio' : 'checkbox'}
              name="scope-result"
              checked={selected(row)}
              disabled={disabled}
              onChange={() => choose(row)}
              className="mt-1 shrink-0 accent-indigo-600"
              aria-label={`Выбрать ${kind === 'theme' ? 'тематику' : 'склейку'} ${row.title}`}
            />
            <div className="min-w-0 flex-1">
              <label htmlFor={`scope-result-${kind}-${row.id}`} className="cursor-pointer">
                <ScopeTitle row={row} />
              </label>
              {row.matches.slice(0, 2).map((match, index) => (
                <MatchPath key={index} match={match} />
              ))}
              {row.matches.length > 2 && (
                <details className="mt-1 text-xs">
                  <summary className="cursor-pointer text-indigo-700 dark:text-indigo-300">
                    Показать ещё совпадения: {row.matches.length - 2}
                  </summary>
                  {row.matches.slice(2).map((match, index) => (
                    <MatchPath key={index} match={match} />
                  ))}
                </details>
              )}
              {row.matchQuality === 'exact' && (
                <span className="text-xs text-indigo-700 dark:text-indigo-300">
                  Точное совпадение
                </span>
              )}
            </div>
            <span className="max-w-24 shrink-0 text-xs tabular-nums">
              {row.cardCount}{' '}
              {kind === 'theme'
                ? marketplace || sellerId || categoryId
                  ? 'по фильтрам поиска'
                  : 'всего'
                : 'карточек'}
            </span>
          </div>
        ))}
        {!loading && page?.items.length === 0 && (
          <p className="p-3 text-sm">Ничего не найдено. Измените запрос или фильтры поиска.</p>
        )}
      </div>
      {Boolean(page?.unbundledMatches?.length) && (
        <div className="rounded border border-amber-200 p-3 text-xs dark:border-amber-900">
          <p>
            Найденные карточки ещё не объединены в склейку. Выберите охват по тематике или сначала
            создайте склейку.
          </p>
          {page?.unbundledMatches?.map((match, i) => (
            <MatchPath key={i} match={match} />
          ))}
        </div>
      )}
      {page && page.total > page.limit && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <Button
            size="sm"
            variant="ghost"
            disabled={loading || !offset}
            onClick={() => setOffset((n) => Math.max(0, n - page.limit))}
          >
            Назад
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + page.items.length, page.total)} из {page.total}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={loading || offset + page.limit >= page.total}
            onClick={() => setOffset((n) => n + page.limit)}
          >
            Далее
          </Button>
        </div>
      )}
      <div
        className="space-y-2 rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50"
        aria-label="Выбранный охват"
      >
        <h4 className="text-sm font-semibold">
          {kind === 'theme' ? 'Выбранная тематика' : `Выбранные склейки: ${ids.length}`}
        </h4>
        {!ids.length && <p className="text-xs">Выберите охват в результатах выше.</p>}
        {ids.map((id) => {
          const row = cache[`${kind}:${id}`]
          return (
            <div key={id} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                {row ? (
                  <ScopeTitle row={row} />
                ) : (
                  <p className="text-sm">
                    {kind === 'theme' ? 'Тематика №' : 'Склейка ID'} {id}
                    <span className="block text-xs text-amber-700 dark:text-amber-400">
                      Не удалось получить описание. Выбор сохранён; доступность проверится перед
                      применением.
                    </span>
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                aria-label={`Убрать ${kind === 'theme' ? 'тематику' : 'склейку'} ${id}`}
                onClick={() =>
                  onChange(
                    kind === 'theme'
                      ? { themeNumber: undefined }
                      : { bundleIds: ids.filter((n) => n !== id) },
                  )
                }
              >
                <X size={14} />
              </Button>
            </div>
          )
        })}
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Фильтры поиска не изменяют выбранный охват.
          {kind === 'theme' && ' Ограничьте реальные карточки ниже.'}
        </p>
      </div>
    </div>
  )
}
